import { effect, inject, Injectable, signal } from '@angular/core';
import { catchError, Observable, of, tap, throwError } from 'rxjs';
import { environment } from "src/environments/environment";
import { HttpClient } from '@angular/common/http';
import { RestService } from 'src/app/services/rest.service';
import { AuthService } from 'src/app/auth/services';
import { PopUp, BottomSheet, BadgePayload } from '../interfaces';
import { AddedBadge } from '../interfaces/badge.interface';
import { Snackbar, TranslationService } from 'src/app/services';

@Injectable({ providedIn: 'root' })
export class GamificationService {  
  // Service injections
  private _authService = inject(AuthService);
  private _rest = inject(RestService);
  private _http = inject(HttpClient);
  private _snackbarService = inject(Snackbar);
  private _translationService = inject(TranslationService);
  
  // Service signal state
  private _passportsEnabled = signal<boolean>(false);
  public passportsEnabled = this._passportsEnabled.asReadonly();

  private _passportPopup = signal<PopUp | null>(null);
  public passportPopup = this._passportPopup.asReadonly();

  private _howItWorks = signal<any | null>(null);
  public howItWorks = this._howItWorks.asReadonly();

  private _whatIsItDescription = signal<any | null>(null);
  public whatIsItDescription = this._whatIsItDescription.asReadonly();
  
  private _addedBadge = signal<AddedBadge | null>(null);
  public addedBadge = this._addedBadge.asReadonly();

  private _passportBottomSheet = signal<BottomSheet | null>(null);
  public passportBottomSheet = this._passportBottomSheet.asReadonly();

  private _sealButtonEnabled = signal<boolean>(false);
  public sealButtonEnabled = this._sealButtonEnabled.asReadonly();

  private _alreadySealed = signal<boolean>(false);
  public alreadySealed = this._alreadySealed.asReadonly();

  private _isSealing = signal<boolean>(false);
  public isSealing = this._isSealing.asReadonly();

  private checkSealButton = effect(() => {
    if (!this.addedBadge())
      this.setSealButton(true);

  }, {allowSignalWrites: true})

  // Local variables
  private apiUrlBase: string = environment.apiUrlBase;
  
  // Setters for service signals
  public setPassportsEnabled(){
    this._passportsEnabled.set(true);
  }

  public setPassportPopup(popup: PopUp){
    this._passportPopup.set(popup);
  }

  public setHowItWorks(howItWorks: any){
    this._howItWorks.set(howItWorks);
  }

  public setWhatIsItDescription(description: any){
    this._whatIsItDescription.set(description);
  }
  
  public setAddedBadge(badge: AddedBadge | null){
    this._addedBadge.set(badge);
  }

  public setSealButton(enabled: boolean){
    this._sealButtonEnabled.set(enabled);
  }

  public setIsSealing(enabled: boolean){
    this._isSealing.set(enabled);
  }

  public setAlreadySealed(enabled: boolean){
    this._alreadySealed.set(enabled);
  }

  public setPassportBottomSheet(bottomSheet: BottomSheet){
    this._passportBottomSheet.set(bottomSheet);
  }
  
  // Methods
  public sealBadgeByQrCodeServiceSignal(qrCode: string) {
    window.location.replace(qrCode);
  }

  public sealBadge(partialPayload: Partial<BadgePayload>): Observable<AddedBadge>{
    const payload: Partial<BadgePayload> = {
      webuser_id: this._authService.user()?.id,
      ...partialPayload
    };
    
    if (this._rest.device()?.client) {
      payload.client_id = this._rest.device()?.client;
    } else if (this._rest.device()?.distributorID) {
      payload.distributor_id = this._rest.device()?.distributorID;
    }

    this.setIsSealing(true);
    return this._http.post<AddedBadge>(`${this.apiUrlBase}/api/gamification/badges/seal`, payload).pipe(
      catchError((err) => {
        this.setIsSealing(false);
        this.setSealButton(true);

        if (err?.error?.message === 'expired'){
          this.setSealButton(false);
          const expiredMessage = this._translationService.instant('Sesión expirada. Vuelva a escanear el código QR');
          this._snackbarService.showErrorSnackbar(expiredMessage);
        }

        if (err?.status === 409) {
          const alreadySealedMessage = this._translationService.instant('Recurso ya sellado previamente');
          this._snackbarService.showErrorSnackbar(alreadySealedMessage);
          this.setAlreadySealed(true);
        }

        return throwError(() => new Error(`Error sealing badge`, { cause: err }));
      })
    );
  }

}




// MOCK DATA ------------------------------------------------------------
//
// return of({
//   "interest_point": {
//       "title": {
//           "es": "Balne\u00e1rio Pedag\u00f3gico de Vidago",
//           "pt": "Balne\u00e1rio Pedag\u00f3gico de Vidago"
//       }
//   },
//   "title": {
//       "es": "Balneario Pedag\u00f3gico de Vidago \u2013 Chaves",
//       "pt": "Balne\u00e1rio Pedag\u00f3gico de Vidago \u2013 Chaves"
//   },
//   "descriptions": [
//       {
//           "es": "<div>Concebido a comienzos del s. XX, fue inaugurado en 1910, a\u00f1o que marc\u00f3 el inicio de la Primera Rep\u00fablica Portuguesa.<\/div>",
//           "pt": "<p>Concebido no in\u00edcio do s\u00e9culo XX, foi inaugurado em 1910, ano que marcou o in\u00edcio da Primeira Rep\u00fablica Portuguesa.<\/p>"
//       },
//       {
//           "es": "<div>Una invitaci\u00f3n a vivir experiencias inolvidables con el encanto de la Belle \u00c9poque.<\/div>",
//           "pt": "<p>Um convite para viver experi\u00eancias inesquec\u00edveis com o encanto da Belle \u00c9poque.<\/p>"
//       }
//   ],
//   "image": {
//       "es": "http:\/\/cicerone-back.test\/storage\/gamification\/passports\/1\/es\/Mg44EvyywW2tYP9nnaLrXjklveCUv5W1P0Y4mZTH.png",
//       "pt": "http:\/\/cicerone-back.test\/storage\/gamification\/passports\/1\/pt\/UX9UHXqcoEHMNVNVdGIv4bvia3mPfnDbMrQnLgox.png"
//   },
//   "passport": {
//       "id": 1
//   }
// }).pipe(
//   catchError((err) => {
//       this._snackbarService.showErrorSnackbar(err.message);
//       return throwError(() => new Error(`Error sealing badge`, { cause: err }));
//   })
// )